import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MobileMenuTrigger } from '@/components/Layout';
import { Search, MapPin, Filter, X, Building2, Flame, TrendingUp, ChevronDown, Sparkles, Gift, Eye, Share2, MessageCircle, Home, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Format price in L or Cr
const formatPrice = (lakh) => {
  if (!lakh || lakh === 0) return '—';
  if (lakh >= 100) {
    return `₹${(lakh / 100).toFixed(lakh % 100 === 0 ? 0 : 1)}Cr`;
  }
  return `₹${lakh}L`;
};

// Get conversion badge
const getConversionBadge = (visits, bookings) => {
  if (visits === 0) return null;
  const rate = ((bookings / visits) * 100).toFixed(0);
  if (rate >= 15) return { color: 'bg-emerald-100 text-emerald-700', label: `${rate}%`, status: 'High' };
  if (rate >= 8) return { color: 'bg-amber-100 text-amber-700', label: `${rate}%`, status: 'Medium' };
  return { color: 'bg-[#F3F0FF] text-[#5B3DF5]', label: `${rate}%`, status: 'Low' };
};

// Status badge colors
const getStatusBadge = (status) => {
  switch (status) {
    case 'Active': return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' };
    case 'Paused': return { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' };
    case 'Sold Out': return { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200' };
    default: return { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200' };
  }
};

const Projects = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('conversion');
  const [showFilters, setShowFilters] = useState(false);
  const [userCity, setUserCity] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 20;
  const navigate = useNavigate();

  const fetchProjects = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = { 
        skip: reset ? 0 : skip, 
        limit,
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search: search })
      };
      
      const response = await api.getProjects(params);
      const data = response.data;
      
      setUserCity(data.user_city || '');
      
      let filteredProjects = data.projects || [];
      
      // Client-side sorting
      if (sortBy === 'conversion') {
        filteredProjects.sort((a, b) => {
          const convA = a.visited_count > 0 ? (a.booked_count / a.visited_count) : 0;
          const convB = b.visited_count > 0 ? (b.booked_count / b.visited_count) : 0;
          return convB - convA;
        });
      } else if (sortBy === 'visits') {
        filteredProjects.sort((a, b) => (b.visited_count || 0) - (a.visited_count || 0));
      } else if (sortBy === 'bookings') {
        filteredProjects.sort((a, b) => (b.booked_count || 0) - (a.booked_count || 0));
      } else if (sortBy === 'price_high') {
        filteredProjects.sort((a, b) => (b.price_range_max || b.price_max_lakh || 0) - (a.price_range_max || a.price_max_lakh || 0));
      }
      
      if (reset) {
        setProjects(filteredProjects);
        setSkip(limit);
      } else {
        setProjects(prev => [...prev, ...filteredProjects]);
        setSkip(prev => prev + limit);
      }
      
      setHasMore(data.total > (reset ? limit : skip + limit));
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sortBy, skip]);

  useEffect(() => {
    fetchProjects(true);
  }, [statusFilter, sortBy]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchProjects(true); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSortBy('conversion');
    setShowFilters(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 md:pb-8">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 md:px-8 md:py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Back Button - Always visible */}
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="page-back-btn"
            >
              <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-[#111827]">Projects</h1>
              <p className="text-xs text-slate-400 hidden sm:block">{userCity ? `${userCity} properties` : 'Property directory'}</p>
            </div>
          </div>
          {/* Hamburger Menu - Mobile */}
          <MobileMenuTrigger user={user} />
        </div>
      </div>

      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-[1400px] mx-auto space-y-4">
        
          {/* Search & Filter Row */}
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            {/* Search Bar */}
            <div className="flex gap-2 flex-1 max-w-xl">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="project-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search project or location..."
                  className="pl-9 h-10 text-sm bg-white border-slate-200 rounded-xl"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X size={14} />
                  </button>
                )}
              </div>
              <Button
                data-testid="filter-toggle"
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`h-10 px-3 rounded-xl md:hidden ${showFilters ? 'bg-[#5B3DF5] hover:bg-[#4A2FE0]' : ''}`}
              >
                <Filter size={16} />
              </Button>
            </div>

            {/* Sort & Filter Controls - Always visible on desktop */}
            <div className="flex gap-2 items-center">
              <span className="text-xs text-slate-500 font-medium">Sort by</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-9 text-xs w-auto min-w-[140px] rounded-lg bg-white border-slate-200 shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conversion">Best Conversion</SelectItem>
                  <SelectItem value="visits">Most Visited</SelectItem>
                  <SelectItem value="bookings">Most Booked</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs w-auto min-w-[100px] rounded-lg bg-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Sold Out">Sold Out</SelectItem>
                </SelectContent>
              </Select>
              
              {(statusFilter || sortBy !== 'conversion') && (
                <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-slate-600">
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Projects Grid */}
          {loading && projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#EF4444] mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <Card className="p-8 text-center bg-white shadow-sm rounded-2xl">
              <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No projects found</p>
              {search && <p className="text-sm text-slate-400 mt-1">Try a different search term</p>}
            </Card>
          ) : (
            <div data-testid="projects-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => {
                const statusStyle = getStatusBadge(project.status);
                const minPrice = project.price_range_min || project.price_min_lakh;
                const maxPrice = project.price_range_max || project.price_max_lakh;
                const priceRange = minPrice && maxPrice
                  ? `${formatPrice(minPrice)} – ${formatPrice(maxPrice)}`
                  : minPrice ? `From ${formatPrice(minPrice)}` : maxPrice ? `Up to ${formatPrice(maxPrice)}` : '—';
                const visitCount = project.visited_count || 0;
                const bookedCount = project.booked_count || 0;
                const convBadge = getConversionBadge(visitCount, bookedCount);
                const avgDealSize = bookedCount > 0 && project.total_booking_value ? formatPrice(project.total_booking_value / bookedCount / 100000) : null;
                
                // Hot project indicators
                const isHot = convBadge?.status === 'High';
                const hasIncentive = project.incentive_live || false;
                const isNewLaunch = project.is_new_launch || false;

                // Quick share handler
                const handleQuickShare = (e) => {
                  e.stopPropagation();
                  const repName = 'Your Advisor';
                  const message = `*${project.project_name}*\n📍 ${project.location}${project.city ? `, ${project.city}` : ''}\n💰 ${priceRange}\n\n📊 Performance:\n👀 ${visitCount} Visits | 🎯 ${bookedCount} Bookings\n\nWould you like to schedule a site visit?`;
                  const encodedMessage = encodeURIComponent(message.trim());
                  window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
                  toast.success('Opening WhatsApp...');
                };

                // Ask SAIRA handler
                const handleAskSaira = (e) => {
                  e.stopPropagation();
                  navigate('/saira', { state: { projectContext: project.project_name, projectId: project.project_id } });
                };
                
                return (
                  <Card
                    key={project.project_id}
                    data-testid={`project-${project.project_id}`}
                    className={`group relative bg-white rounded-2xl cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] overflow-hidden ${
                      isHot ? 'border-l-4 border-l-orange-400 shadow-md' : 'shadow-sm hover:shadow-md'
                    }`}
                    onClick={() => navigate(`/projects/${project.project_id}`)}
                  >
                    {/* Hover Quick Actions Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 flex flex-col justify-end p-3 pointer-events-none group-hover:pointer-events-auto">
                      <div className="flex gap-2">
                        <Button
                          data-testid={`quick-view-${project.project_id}`}
                          size="sm"
                          className="flex-1 h-8 bg-white/95 hover:bg-white text-slate-800 text-xs font-medium rounded-lg shadow-sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.project_id}`); }}
                        >
                          <Eye size={12} className="mr-1" />
                          View Details
                        </Button>
                        <Button
                          data-testid={`quick-share-${project.project_id}`}
                          size="sm"
                          className="h-8 bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-medium rounded-lg px-3"
                          onClick={handleQuickShare}
                        >
                          <Share2 size={12} />
                        </Button>
                        <Button
                          data-testid={`quick-saira-${project.project_id}`}
                          size="sm"
                          className="h-8 bg-[#5B3DF5] hover:bg-[#4A2FE0] text-white text-xs font-medium rounded-lg px-3"
                          onClick={handleAskSaira}
                        >
                          <MessageCircle size={12} />
                        </Button>
                      </div>
                    </div>

                    <div className="p-4 flex flex-col h-full">
                      {/* Project Name & Tags */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <h3 className="font-bold text-[15px] text-[#111827] leading-tight group-hover:text-[#5B3DF5] transition-colors">{project.project_name}</h3>
                            {isHot && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><Flame size={9}/>Hot</span>}
                            {hasIncentive && <span className="text-[10px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><Gift size={9}/>Incentive</span>}
                            {isNewLaunch && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5"><Sparkles size={9}/>New</span>}
                          </div>
                          
                          {/* Location */}
                          <div className="flex items-center gap-1.5">
                            <MapPin size={11} className="text-slate-400 flex-shrink-0" />
                            <span className="text-xs text-slate-500 truncate">
                              {project.location}{project.city ? `, ${project.city}` : ''}
                            </span>
                          </div>
                        </div>
                        
                        {/* Status Badge */}
                        <span className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border} whitespace-nowrap`}>
                          {project.status}
                        </span>
                      </div>
                      
                      {/* Price Section */}
                      <div className="mb-3">
                        <p className="text-lg font-bold text-[#5B3DF5]">{priceRange}</p>
                        {avgDealSize && <p className="text-[11px] text-slate-400 mt-0.5">Avg Deal Size: {avgDealSize}</p>}
                      </div>
                      
                      {/* Divider */}
                      <div className="border-t border-slate-100 my-2"></div>
                      
                      {/* Performance Metrics */}
                      <div className="flex items-center gap-3 mt-auto pt-1">
                        <div className="text-center flex-1">
                          <p className="text-base font-bold text-slate-700">{visitCount}</p>
                          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Visits</p>
                        </div>
                        <div className="w-px h-8 bg-slate-100"></div>
                        <div className="text-center flex-1">
                          <p className={`text-base font-bold ${bookedCount > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>{bookedCount}</p>
                          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Bookings</p>
                        </div>
                        {convBadge && (
                          <>
                            <div className="w-px h-8 bg-slate-100"></div>
                            <div className="text-center flex-1">
                              <p className={`text-base font-bold px-2.5 py-0.5 rounded-full ${convBadge.color}`}>{convBadge.label}</p>
                              <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Conv.</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !loading && (
            <div className="flex justify-center pt-4">
              <Button 
                data-testid="load-more" 
                variant="outline" 
                onClick={() => fetchProjects(false)} 
                disabled={loading} 
                className="px-8 rounded-xl"
              >
                {loading ? 'Loading...' : 'Load More Projects'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Projects;
